/**
 * Pure geometry/colour helpers for the Fabric.js floor plan editor —
 * no Fabric.js canvas dependency, safe to unit test in isolation.
 */
import { Room } from '../../types/Planner';
import { presetFor } from './elementPresets';
import { SEAT_LIMITS, CHAIR_GAP as TG_CHAIR_GAP, CHAIR_H as TG_CHAIR_H, CHAIR_W as TG_CHAIR_W, seatPositions, tableLabel } from './tableGeometry';
import type { Direction } from './tableGeometry';
import { sectionLetterForRoom } from './sectionGeometry';

export const SNAP = 10;
export const snap = (v: number) => Math.round(v / SNAP) * SNAP;

export const ROOM_COLORS: Record<string, string> = {
    ceremony:  '#e8d5b7',
    reception: '#d4e8d7',
    cocktail:  '#d7e4f5',
    dining:    '#f5e8d7',
    dance:     '#f0d7f5',
    stage:     '#d7f0f5',
    entrance:  '#f5f0d7',
    restroom:  '#e8e8e8',
    kitchen:   '#f5d7d7',
    other:     '#e0e0e0',
    table:     '#8b5a3c',
};

// Neutral stand-in (not a hex colour) used when a design token cannot be read,
// e.g. in unit tests or before the stylesheet has loaded.
const TOKEN_FALLBACK = 'rgb(224, 224, 224)';

/** Current value of a design-system CSS custom property, for painting on a canvas. */
export function resolveToken(name: string, fallback: string = TOKEN_FALLBACK): string {
    if (typeof document === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value && !value.startsWith('var(') ? value : fallback;
}

export function roomColor(type: Room['type'], room?: Partial<Room>): string {
    if (room?.customColor) return room.customColor;
    if (room?.groupColor) return room.groupColor;
    if (ROOM_COLORS[type]) return ROOM_COLORS[type];
    const preset = presetFor(type);
    return preset ? resolveToken(preset.tone) : ROOM_COLORS.other;
}

// Fill badge: use namedSeats for head tables; use guestGroupCounts for regular
// tables. Ported from FloorPlanEditor.tsx's identical logic (GAP 2).
export function computeFillBadge(
    room: Room,
    guestGroupCounts: Record<string, number>,
): { show: boolean; filled: number; total: number | undefined } {
    const total = room.seats ?? room.capacity;
    let filled: number;
    let show: boolean;
    if (room.isHeadTable) {
        filled = (room.namedSeats ?? []).filter(s => s.guestName.trim()).length;
        show = total != null;
    } else if (room.groupId && guestGroupCounts[room.groupId] !== undefined) {
        filled = guestGroupCounts[room.groupId];
        show = total != null;
    } else {
        filled = (room.namedSeats ?? []).filter(s => s.guestName.trim()).length;
        show = room.groupId != null && total != null;
    }
    return { show, filled, total };
}

// ── Chair layout ──────────────────────────────────────────────────────────
// Ported from FloorPlanEditor.tsx's renderChairs. That version positioned
// chairs relative to a wrapper div; Fabric objects use absolute canvas
// coordinates instead, so positions here are computed directly from
// room.position rather than an offset wrapper.
export const CHAIR_W = TG_CHAIR_W;
export const CHAIR_H = TG_CHAIR_H;
export const CHAIR_GAP = TG_CHAIR_GAP;

export interface ChairPosition {
    left: number;
    top: number;
    angleRad: number; // rotation to apply, radians
}

/**
 * Chair positions for a table. Round/oval/rect keep their original layout;
 * crescent and rectangular come from tableGeometry. `facing` is only used by a
 * crescent (which way its hollow side faces; see layoutContext.crescentFacing).
 */
export function computeChairPositions(room: Room, facing: Direction = 'up'): ChairPosition[] {
    const shape = room.tableShape ?? 'round';
    const fallback = shape === 'rect' ? 6 : shape === 'crescent' || shape === 'rectangular' ? SEAT_LIMITS[shape].default : 8;
    const seats = room.seats ?? room.capacity ?? fallback;
    return seatPositions(shape, { x: room.position.x, y: room.position.y, width: room.width, height: room.height }, seats, facing);
}

/** Section letter (A to D) for a table, from where it sits among the plan's section zones. */
export function sectionLetterOf(room: Room, rooms: ReadonlyArray<Room>): string | null {
    return sectionLetterForRoom(room, rooms);
}

/** Text drawn on a room: tables show their number and section letter, everything else its name. */
export function labelForRoom(room: Room, rooms: ReadonlyArray<Room>): string {
    return room.type === 'table' ? tableLabel(room.name, sectionLetterOf(room, rooms)) : room.name;
}
